import type { CsvNormalizedRow, TransactionKind } from "@financas/contracts";

export interface CsvParseResult {
  rows: CsvNormalizedRow[];
  errors: Array<{ rowNumber: number; message: string }>;
  detectedPositiveAsExpense: boolean;
}

function normalizeHeader(value: string): string {
  return value.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();
}

function parseLines(text: string, delimiter: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = "";
  let quoted = false;
  for (let index = 0; index < text.length; index += 1) {
    const char = text[index]!;
    if (char === '"') {
      if (quoted && text[index + 1] === '"') {
        field += '"';
        index += 1;
      } else quoted = !quoted;
    } else if (char === delimiter && !quoted) {
      row.push(field.trim());
      field = "";
    } else if ((char === "\n" || char === "\r") && !quoted) {
      if (char === "\r" && text[index + 1] === "\n") index += 1;
      row.push(field.trim());
      field = "";
      if (row.some(Boolean)) rows.push(row);
      row = [];
    } else field += char;
  }
  row.push(field.trim());
  if (row.some(Boolean)) rows.push(row);
  return rows;
}

function parseDate(value: string): string | null {
  const iso = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`;
  const br = value.match(/^(\d{1,2})[\/-](\d{1,2})[\/-](\d{2,4})/);
  if (!br) return null;
  const year = br[3]!.length === 2 ? `20${br[3]}` : br[3]!;
  const month = br[2]!.padStart(2, "0");
  const day = br[1]!.padStart(2, "0");
  const date = new Date(`${year}-${month}-${day}T12:00:00Z`);
  return Number.isNaN(date.getTime()) ? null : `${year}-${month}-${day}`;
}

function parseAmount(value: string): number | null {
  const cleaned = value.replace(/R\$/gi, "").replace(/\s/g, "");
  const decimal = cleaned.includes(",")
    ? cleaned.replaceAll(".", "").replace(",", ".")
    : cleaned;
  const number = Number(decimal);
  return Number.isFinite(number) && number !== 0 ? Math.round(number * 100) : null;
}

function pickIndex(headers: string[], aliases: string[]): number {
  return headers.findIndex((header) => aliases.includes(header));
}

export function parseNubankCsv(text: string, positiveAsExpense?: boolean): CsvParseResult {
  const cleanText = text.replace(/^\uFEFF/, "");
  const firstLine = cleanText.split(/\r?\n/, 1)[0] ?? "";
  const delimiter = (firstLine.match(/;/g)?.length ?? 0) > (firstLine.match(/,/g)?.length ?? 0) ? ";" : ",";
  const table = parseLines(cleanText, delimiter);
  if (table.length < 2) return { rows: [], errors: [{ rowNumber: 1, message: "CSV vazio ou sem cabeçalho." }], detectedPositiveAsExpense: true };
  const headers = table[0]!.map(normalizeHeader);
  const dateIndex = pickIndex(headers, ["date", "data", "data da transacao"]);
  const descriptionIndex = pickIndex(headers, ["title", "titulo", "description", "descricao", "estabelecimento"]);
  const amountIndex = pickIndex(headers, ["amount", "valor", "valor da transacao"]);
  const idIndex = pickIndex(headers, ["id", "identificador", "transaction id"]);
  const categoryIndex = pickIndex(headers, ["category", "categoria"]);
  const detectedPositiveAsExpense = headers.includes("title") || headers.includes("titulo");
  const treatPositiveAsExpense = positiveAsExpense ?? detectedPositiveAsExpense;
  if (dateIndex < 0 || descriptionIndex < 0 || amountIndex < 0) {
    return {
      rows: [],
      errors: [{ rowNumber: 1, message: "Cabeçalhos esperados: data/date, descrição/title e valor/amount." }],
      detectedPositiveAsExpense
    };
  }
  const rows: CsvNormalizedRow[] = [];
  const errors: CsvParseResult["errors"] = [];
  table.slice(1).forEach((fields, index) => {
    const rowNumber = index + 2;
    const economicDate = parseDate(fields[dateIndex] ?? "");
    const signedAmount = parseAmount(fields[amountIndex] ?? "");
    const description = (fields[descriptionIndex] ?? "").trim();
    if (!economicDate || signedAmount === null || !description) {
      errors.push({ rowNumber, message: "Data, valor ou descrição inválidos." });
      return;
    }
    let kind: TransactionKind;
    if (treatPositiveAsExpense) kind = signedAmount > 0 ? "expense" : "refund";
    else kind = signedAmount > 0 ? "income" : "expense";
    const row: CsvNormalizedRow = {
      rowNumber,
      economicDate,
      amountMinor: Math.abs(signedAmount),
      kind,
      description: description.slice(0, 240)
    };
    const externalRef = idIndex >= 0 ? fields[idIndex]?.trim() : undefined;
    const categoryHint = categoryIndex >= 0 ? fields[categoryIndex]?.trim() : undefined;
    if (externalRef) row.externalRef = externalRef;
    if (categoryHint) row.categoryHint = categoryHint;
    rows.push(row);
  });
  return { rows, errors, detectedPositiveAsExpense };
}

